import { Injectable, Injector } from '@angular/core';
import { Query } from '@datorama/akita';
import { RxNote } from '@modules/notes/models/rx-note';
import { DoctorsRxConfiguration } from '@shared/models/doctors-rx-configuration';
import { IdName } from '@shared/models/id-name';
import { LinkedCompany } from '@shared/models/linked-company';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { ScannerRxConfig } from '@shared/models/scanner-rx-config';
import { UserSettings } from '@shared/models/user-settings';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { ShellState, ShellStore } from './shell-store';
import { ImplantMasterList } from '@shared/models/implant-master-list';
import { PrintOrientation, RxVersion } from '@shared/models/enums/enums';
import { FeatureToggle } from '@shared/models/feature-toggle';
import { Material } from '@shared/models/material';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { Doctor } from '@shared/models/doctor';
import { EnvironmentParams } from '@shared/models/environment-params';
import { getDateFormatOption } from '@shared/utils/date-format-util';
import { ProcedureMap } from '@shared/models/procedure-map';
import { Procedure } from '@shared/models/procedure';
import { RxComponents } from '@shared/models/rx-models/interfaces/rx-components-model';
import { RoleTypeEnum } from '@shared/models/role-type';
import { TreatmentStage } from '@shared/models/treatment-stage';
import { CaseType } from '@shared/models/case-type';
import { Specification } from '@shared/models/specification';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { ScannerVersion } from '@shared/models/company-scanner';
import { RegionClusterMapper } from '@shared/utils/region-cluster-mapper';
import { LinkedCompaniesBySendToType, LinkedCompanyDto } from '@shared/models/linked-companies';
import type { Maybe } from '@/types/globals';
import { ViewModeEnum } from '@shared/models/consts';

@Injectable({ providedIn: 'root' })
export class ShellQuery extends Query<ShellState> {
	isRxPending$: Observable<boolean> = this.select(state => state.isRxPending);
	shellState$: Observable<ShellState> = this.select(state => state);
	isLoading$: Observable<boolean> = this.select(state => state.isLoading);
	languageCode$: Observable<string> = this.select(state => state.languageCode);
	isReadOnly$: Observable<boolean> = this.select(state => state.isReadOnly);
	readOnlyRules$: Observable<Maybe<RxComponents>> = this.select(state => state.readOnlyRules);
	validationMode$: Observable<Maybe<string>> = this.select(state => state.validationMode);
	isRxTakenForScan$: Observable<Maybe<boolean>> = this.select(state => state.isRxTakenForScan);
	hasMultipleScanLayers$: Observable<boolean> = this.select(state => !!state.hasMultipleScanLayers);
	preTreatmentScanAttempted$: Observable<boolean> = this.select(state => state.preTreatmentScanAttempted);
	shouldAnonymizeRx$: Observable<boolean> = this.select(state => state.shouldAnonymizeRx);

	rx$: Observable<Maybe<RxModel>> = this.select(state => state.rx);
	clonedRx$: Observable<Maybe<RxModel>> = this.select(state => state.clonedRx);
	teeth$: Observable<Maybe<Tooth[]>> = this.select(state => state.rx?.Teeth);
	dentureDetails$ = this.select(state => state.rx?.DentureDetails);
	applianceDetails$ = this.select(state => state.rx?.ApplianceDetails);
	isProcedureChanged$ = this.select(state => state.isProcedureChanged);
	isOnline$: Observable<boolean | null> = this.select(state => state.isOnline);

	staticFilesEndpoint$: Observable<string> = this.select(state => state.staticFilesEndpoint);
	rxId$: Observable<Maybe<string>> = this.select(state => state.rxId);
	isReturned$: Observable<boolean> = this.select(state => state.isReturn).pipe(shareReplay({ bufferSize: 1, refCount: true }));
	isResendScan$: Observable<boolean> = this.select(state => state.isResendScan);
	orderId$: Observable<Maybe<number>> = this.select(state => state.orderId);
	clonedFromRxId$: Observable<Maybe<string>> = this.select(state => state.clonedFromRxId);
	isCloningMode$: Observable<boolean> = this.select(state => !state.isReadOnly && (!!state.clonedFromRxId || !!state.rx?.ClonedFromRxID));
	enableAllCaseTypesForAddRx$: Observable<boolean> = this.select(state => state.enableAllCaseTypesForAddRx);
	isRxSent$: Observable<boolean> = this.select(state => (state.orderId ? !!state.orderId : !!state.rx?.Order?.ID));
	preparedNewRxId$: Observable<Maybe<string>> = this.select(state => state.preparedNewRxId);

	attachmentsRxId$: Observable<Maybe<string>> = this.select(state => [state.rxId, state.orderId]).pipe(
		switchMap(([rxId, orderId]) => {
			if (rxId) {
				return of(rxId.toString());
			}

			// If we have orderId we  need to wait for rx anyway and take rxId from it
			if (orderId) {
				return this.rx$.pipe(
					filter((x): x is RxModel => !!x),
					take(1),
					map(x => x.ID)
				);
			}

			return this.preparedNewRxId$;
		}),
		distinctUntilChanged()
	);

	isRxScanned$: Observable<boolean> = this.select(state => !!state.rx?.Order?.ScanDate);
	shipToId$ = this.select(state => state.rx?.Order?.ShipToId);
	isNewAndNotClonedRx$: Observable<boolean> = this.select(state => this.checkIsNewAndNotClonedRx(state));

	rxConfiguration$: Observable<Maybe<RxConfiguration>> = this.select(state => state.rxConfiguration);
	userSettings$: Observable<Maybe<UserSettings>> = this.select(state => state.userSettings);

	license$: Observable<string | undefined> = this.select(state => state.userSettings?.LicenseNumber);
	rxForDoctorData$: Observable<RxModel | null | undefined> = this.select(state => state.rx);
	directToLab$: Observable<Maybe<boolean>> = this.select(state => state.rx?.Order?.DirectToLab);
	// IMPORTANT! There is no new beautiful NotesArray on the BFF. We have to convert ugly string to array every time
	notesArray$: Observable<Maybe<RxNote[]>> = this.select(state => state.rx?.NotesArray);
	notes$: Observable<Maybe<string>> = this.select(state => state.rx?.Notes);
	alignTechNotes$: Observable<Maybe<string>> = this.select(state => state.rx?.AligntechNotes);
	alignTechNotesArray$: Observable<Maybe<RxNote[]>> = this.select(state => state.rx?.AlignTechNotesArray);

	contactId$: Observable<Maybe<number>> = this.select(state => state.contactId);
	companyId$: Observable<Maybe<number>> = this.select(state => state.companyId);

	doctor$: Observable<Doctor> = combineLatest([
		this.select(state => state.rxConfiguration?.Doctors),
		this.contactId$,
		this.rx$,
		this.license$
	]).pipe(
		map(([doctors, contactId, rx, license]) => {
			return [this.getCurrentDoctor(doctors, contactId, rx), license];
		}),
		map(([currentDoctor, license]: [Doctor, string]) => {
			return { Id: currentDoctor?.Id, Name: currentDoctor?.Name, Licence: currentDoctor?.Licence || license };
		})
	);
	user$: Observable<IdName | null | undefined> = this.select(state => state.user);

	isRxValidForSave$: Observable<boolean | null | undefined> = this.select(state => state.isRxValidForSave);

	shouldValidateForSend$: Observable<boolean> = this.select(state => this.checkWhetherShouldValidateForSend(state));

	companyCountryCode$: Observable<string | undefined> = this.select(state => state.rxConfiguration?.CompanyConfiguration?.CountryCode);

	productType$: Observable<string | null | undefined> = this.select(state => state.productType);

	applicationMode$: Observable<string | null | undefined> = this.select(state => state.applicationMode);

	isHeadlessPrint$: Observable<boolean> = this.select(state => state.isHeadlessPrint);

	apiEndpoint$: Observable<string | null | undefined> = this.select(state => state.apiEndpoint);

	rxAppApiEndpoint$: Observable<string | null | undefined> = this.select(state => state.rxAppApiEndpoint);

	patientAppApiEndpoint$: Observable<string | null | undefined> = this.select(state => state.patientAppApiEndpoint);

	region$: Observable<string | null | undefined> = this.select(state => state.region);

	caseTypes$: Observable<CaseType[]> = this.select(state => state.rxConfiguration?.CaseTypes).pipe(
		filter((caseTypes): caseTypes is CaseType[] => !!caseTypes)
	);
	defaultCaseTypeId$: Observable<number> = this.select(state => state.userSettings?.CaseTypeId).pipe(
		filter((caseTypeId): caseTypeId is number => !!caseTypeId)
	);

	procedures$: Observable<Procedure[]> = this.select(state => state.rxConfiguration?.Procedures).pipe(
		filter((procedures): procedures is Procedure[] => !!procedures)
	);
	types$: Observable<IdName[]> = this.select(state => state.rxConfiguration?.Types).pipe(filter((types): types is IdName[] => !!types));
	ponticDesigns$: Observable<IdName[]> = this.select(state => state.rxConfiguration?.PonticDesigns).pipe(
		filter((ponticDesigns): ponticDesigns is IdName[] => !!ponticDesigns)
	);
	defaultProcedure$: Observable<number> = this.select(state => state.userSettings?.DefaultProcedureId).pipe(
		filter((procedureId): procedureId is number => !!procedureId)
	);
	defaultShade$: Observable<number> = this.select(state => state.userSettings?.ShadeSystemId).pipe(
		filter((shadeId): shadeId is number => !!shadeId)
	);

	proceduresMap$: Observable<ProcedureMap[]> = this.select(state => {
		return state.rxConfiguration?.RxRules?.ProceduresMap;
	}).pipe(filter((proceduresMap): proceduresMap is ProcedureMap[] => !!proceduresMap));
	procedureTreatmentStages$: Observable<Maybe<TreatmentStage[]>> = this.select(state => state.rxConfiguration?.TreatmentStages);
	dateFormat$: Observable<string> = this.select(state => state.userSettings?.DateFormat).pipe(
		filter((dateFormat): dateFormat is string => !!dateFormat),
		map(dateFormat => getDateFormatOption(dateFormat))
	);
	/**
	 * @deprecated OrthoLabs are no longer used, use linkedCompanies$[SendToTypeEnum.restoLab] instead
	 */
	dentalLabs$: Observable<LinkedCompany[]> = this.select(state => state.rxConfiguration?.LinkedCompanies?.DentalLabs).pipe(
		filter((labs): labs is LinkedCompany[] => !!labs)
	);
	directToLabs$: Observable<LinkedCompany[]> = this.select(state => state.rxConfiguration?.LinkedCompanies?.DirectToLabs).pipe(
		filter((labs): labs is LinkedCompany[] => !!labs)
	);
	/**
	 * @deprecated OrthoLabs are no longer used, use linkedCompanies$[SendToTypeEnum.orthoLab] instead
	 */
	orthoLabs$: Observable<LinkedCompany[]> = this.select(state => state.rxConfiguration?.LinkedCompanies?.OrthoLabs).pipe(
		filter((labs): labs is LinkedCompany[] => !!labs)
	);
	/**
	 * @deprecated Use linkedCompanies$ instead
	 */
	allLabs$: Observable<LinkedCompany[]> = combineLatest([this.dentalLabs$, this.directToLabs$, this.orthoLabs$]).pipe(
		map(([dentalLabs, directToLabs, orthoLabs]) => [...dentalLabs, ...directToLabs, ...orthoLabs])
	);

	linkedCompaniesBySendToType$: Observable<Maybe<LinkedCompaniesBySendToType>> = this.select(state => state.linkedCompanies);

	linkedCompanies$: Observable<LinkedCompanyDto[]> = this.linkedCompaniesBySendToType$.pipe(
		filter((linkedCompanies): linkedCompanies is LinkedCompaniesBySendToType => !!linkedCompanies),
		map(linkedCompanies => Object.values(linkedCompanies).reduce((acc, current) => acc.concat(...current), []))
	);

	companies$: Observable<IdName<number>[]> = combineLatest({
		linkedCompanies: this.linkedCompanies$,
		directToLabs: this.directToLabs$,
		allLabs: this.allLabs$
	}).pipe(
		map(({ linkedCompanies, directToLabs, allLabs }) => (linkedCompanies.length > 0 ? [...linkedCompanies, ...directToLabs] : allLabs))
	);

	scanners$: Observable<Maybe<ScannerRxConfig[]>> = this.select(state => state.rxConfiguration?.CompanyConfiguration?.Scanners);
	scannersVersions$: Observable<Maybe<ScannerVersion[]>> = this.select(state => state.scannersVersions);

	niriCaptureDefaultValue$: Observable<boolean> = this.select(state => state.userSettings?.NIRIEnabledDefault).pipe(
		filter((niriCaptureDefaultValue): niriCaptureDefaultValue is boolean => typeof niriCaptureDefaultValue === 'boolean')
	);
	palatalFeedbackDefaultValue$: Observable<boolean> = this.select(state => state.userSettings?.PalatalScanningFeedback ?? false);

	niriEnabled$: Observable<boolean | undefined> = this.select(state => state.rx?.NIRIEnabled);
	isNiriCaptureVisible$: Observable<Maybe<boolean>> = this.select(state => state.isNiriCaptureVisible);
	isSleeveConfirmed$: Observable<Maybe<boolean>> = this.select(state => state.rx?.IsSleeveConfirmed);
	isSleeveConfirmationVisible$: Observable<Maybe<boolean>> = this.select(state => state.isSleeveConfirmationVisible);
	isDentureCopyScan$: Observable<boolean | undefined> = this.select(state => state.rx?.DentureDetails?.IsDentureCopyScan);
	prePrepScan$: Observable<boolean | undefined> = this.select(state => state.rx?.PrePrepScan);
	emergenceProfile$: Observable<boolean | undefined> = this.select(state => state.rx?.EmergenceProfile);
	palatalScanningFeedback$: Observable<boolean | undefined | null> = this.select(state => state.rx?.PalatalScanningFeedback);

	isPreTreatmentVisibleForV0$: Observable<Maybe<boolean>> = this.select(state => state.isPreTreatmentVisibleForV0);
	isPalatalFeedbackVisible$: Observable<Maybe<boolean>> = this.select(state => state.isPalatalFeedbackVisible);

	isScanOptionsVisible$ = combineLatest([
		this.isNiriCaptureVisible$,
		this.isSleeveConfirmationVisible$,
		this.isPreTreatmentVisibleForV0$,
		this.isPalatalFeedbackVisible$
	]).pipe(
		map(([isNiriCaptureVisible, isSleeveConfirmationVisible, isPreTreatmentVisible, isPalatalFeedbackVisible]) => {
			return isNiriCaptureVisible || isSleeveConfirmationVisible || isPreTreatmentVisible || isPalatalFeedbackVisible;
		})
	);

	featureToggles$: Observable<Maybe<FeatureToggle[]>> = this.select(state => state.featureToggles);

	companySoftwareOptions$: Observable<Maybe<number[]>> = this.select(
		state => state.rxConfiguration?.CompanyConfiguration?.SoftwareOptionsForCompany
	);

	isSleeveConfirmationReadOnly$: Observable<boolean> = combineLatest([
		this.isReturned$,
		this.isRxSent$,
		this.getReadOnlyState('scanOptions')
	]).pipe(
		map(([isReturned, isRxSent, isReadOnly]) => {
			return !isReturned && (isRxSent || isReadOnly);
		})
	);

	scannerId$: Observable<Maybe<string>> = this.select(state => state.scannerId);

	scanner$: Observable<Maybe<ScannerRxConfig>> = combineLatest([this.scannerId$, this.scanners$]).pipe(
		map(([scannerId, scanners]) => {
			const result: Maybe<ScannerRxConfig> = scanners?.find(x => x.ResourceGuid === scannerId);

			return result ?? null;
		})
	);

	clientVersion$: Observable<Maybe<string>> = this.select(state => state.clientVersion);
	packageVersion$: Observable<Maybe<string>> = this.select(state => state.packageVersion);
	/**
	 * @deprecated
	 */
	highestPackageVersion$ = this.select(state => state.rxConfiguration?.HighestScannerVersion);

	forceV0$: Observable<Maybe<boolean>> = this.select(state => state.forceV0);

	implantMasterList$: Observable<ImplantMasterList | undefined> = this.select(state => state.rxConfiguration?.ImplantMasterList);

	materials$: Observable<Material[] | undefined> = this.select(state => state.rxConfiguration?.Materials);
	specifications$: Observable<Specification[] | undefined> = this.select(state => state.rxConfiguration?.Specifications);

	appSettings$: Observable<EnvironmentParams> = this.select(state => state.appSettings);

	rxappBffUrl$: Observable<string | null> = this.select(state => ({ region: state.region, appSettings: state.appSettings })).pipe(
		map(({ region, appSettings }) => {
			const regionClusterMapper = this.injector.get(RegionClusterMapper);

			return regionClusterMapper.resolveBffUrl(appSettings, region);
		})
	);

	regulatoryDOBMask$: Observable<string | undefined> = this.select(
		state => state.rxConfiguration?.CompanyConfiguration?.RegulatorySettings?.DOBMask
	);
	maxPatientLastNameLength$: Observable<number | undefined> = this.select(
		state => state.rxConfiguration?.CompanyConfiguration?.RegulatorySettings?.MaxPatientLastNameLength
	);
	patientAppUrl$: Observable<Maybe<string>> = this.select(state => state.appSettings).pipe(
		map(appSettings => appSettings?.patientAppUrl)
	);
	linkToTheLearnNowForDesignSuite$: Observable<Maybe<string>> = this.select(state => state.appSettings).pipe(
		map(appSettings => appSettings?.linkToTheLearnNowForDesignSuite)
	);
	authToken$: Observable<Maybe<string>> = this.select(state => state.authToken);

	userRole$: Observable<RoleTypeEnum> = this.select(state => state.userRole);
	rxVersionFlow$: Observable<RxVersion> = this.select(state => state.rxVersionFlow);
	isProcedureFlow$: Observable<boolean> = combineLatest([this.rxVersionFlow$, this.forceV0$]).pipe(
		map(([rxVersion, forceV0]) => !forceV0 && this.checkIsProcedureFlow(rxVersion))
	);

	restorationTypes$: Observable<IdName[] | undefined> = this.select(state => state.rxConfiguration?.RestorationTypes);

	rxsForPrint$ = this.select(state => state.rxsForPrint);

	viewMode$: Observable<Maybe<ViewModeEnum>> = this.select(state => state.viewMode);

	get flowRxStep(): string | undefined {
		return this.getValue().flowRxStep;
	}

	appBISessionId$: Observable<string | undefined> = this.select(state => state.appBISessionId);

	flowRxStep$ = this.select(state => state.flowRxStep);

	labBrokerSendStepBypassed$ = this.select(state => state.labBrokerSendStepBypassed);

	get rxSessionId(): Maybe<string> {
		return this.getValue().rxSessionId;
	}

	get appBISessionId(): string | undefined {
		return this.getValue().appBISessionId;
	}

	get rxConfiguration(): Maybe<RxConfiguration> {
		return this.getValue().rxConfiguration;
	}
	get dentalLabs(): LinkedCompany[] | undefined {
		return this.rxConfiguration?.LinkedCompanies?.DentalLabs;
	}
	get directToLabs(): LinkedCompany[] | undefined {
		return this.rxConfiguration?.LinkedCompanies?.DirectToLabs;
	}
	get orthoLabs(): LinkedCompany[] | undefined {
		return this.rxConfiguration?.LinkedCompanies?.OrthoLabs;
	}
	get companiesBySendToType(): Maybe<LinkedCompaniesBySendToType> {
		return this.getValue().linkedCompanies;
	}
	get caseTypes(): IdName[] | undefined {
		return this.rxConfiguration?.CaseTypes;
	}
	get procedureMaps(): ProcedureMap[] | undefined {
		return this.rxConfiguration?.RxRules?.ProceduresMap;
	}
	get implantMasterList(): ImplantMasterList | undefined {
		return this.rxConfiguration?.ImplantMasterList;
	}
	get softwareOptionsForCompany(): Maybe<number[]> {
		return this.rxConfiguration?.CompanyConfiguration?.SoftwareOptionsForCompany;
	}
	get materials(): Material[] | undefined {
		return this.rxConfiguration?.Materials;
	}
	get specifications(): Specification[] | undefined {
		return this.rxConfiguration?.Specifications;
	}
	get ponticDesigns(): IdName[] | undefined {
		return this.rxConfiguration?.PonticDesigns;
	}
	get restorationTypes(): IdName[] | undefined {
		return this.rxConfiguration?.RestorationTypes;
	}
	get abutmentTypes(): IdName[] | undefined {
		return this.rxConfiguration?.AbutmentTypes;
	}

	get userSettings(): Maybe<UserSettings> {
		return this.getValue().userSettings;
	}
	get defaultShadeSystemId(): number | undefined {
		return this.getValue().userSettings?.ShadeSystemId;
	}
	get dateFormat(): string | undefined {
		return this.userSettings?.DateFormat;
	}
	get license(): string | undefined {
		return this.userSettings?.LicenseNumber;
	}
	get contactId(): Maybe<number> {
		return this.getValue().contactId;
	}
	get user(): IdName | null | undefined {
		return this.getValue().user;
	}
	get companyId(): Maybe<number> {
		return this.getValue().companyId;
	}

	get clientVersion(): Maybe<string> {
		return this.getValue().clientVersion;
	}

	get packageVersion(): Maybe<string> {
		return this.getValue().packageVersion;
	}

	get scannersVersions(): Maybe<ScannerVersion[]> {
		return this.getValue().scannersVersions;
	}

	get isNiriCaptureVisible(): Maybe<boolean> {
		return this.getValue().isNiriCaptureVisible;
	}
	get applicationMode(): Maybe<string> {
		return this.getValue().applicationMode;
	}
	get apiEndpoint(): Maybe<string> {
		return this.getValue().apiEndpoint;
	}
	get rxAppApiEndpoint(): Maybe<string> {
		return this.getValue().rxAppApiEndpoint;
	}
	get patientAppApiEndpoint(): Maybe<string> {
		return this.getValue().patientAppApiEndpoint;
	}
	get staticFilesEndpoint(): string {
		return this.getValue().staticFilesEndpoint;
	}
	get orderId(): Maybe<number> {
		return this.getValue().orderId;
	}
	get rxId(): Maybe<string> {
		return this.getValue().rxId;
	}
	get preparedNewRxId(): Maybe<string> {
		return this.getValue().preparedNewRxId;
	}
	get languageCode(): string {
		return this.getValue().languageCode;
	}
	get rx(): Maybe<RxModel> {
		return this.getValue().rx;
	}
	get isRxPending(): boolean {
		return this.getValue().isRxPending;
	}
	get clonedRx(): Maybe<RxModel> {
		return this.getValue().clonedRx;
	}
	get isReadOnly(): boolean {
		return this.getValue().isReadOnly;
	}
	get authToken(): Maybe<string> {
		return this.getValue().authToken;
	}
	get readOnlyRules(): Maybe<RxComponents> {
		return this.getValue().readOnlyRules;
	}
	get isNewAndNotClonedRx(): boolean {
		return this.checkIsNewAndNotClonedRx(this.getValue());
	}
	get isRxValidForSave(): Maybe<boolean> {
		return this.getValue().isRxValidForSave;
	}
	get rxsForPrint(): Maybe<RxModel[]> {
		return this.getValue().rxsForPrint;
	}
	get printOrientation(): Maybe<PrintOrientation> {
		return this.getValue().printOrientation;
	}
	get isHeadlessPrint(): boolean {
		return this.getValue().isHeadlessPrint;
	}
	get clonedFromRxID(): Maybe<string> {
		return this.getValue().clonedFromRxId;
	}
	get doctor(): Maybe<Doctor> {
		const currentDoctor = this.getCurrentDoctor(this.rxConfiguration?.Doctors, this.contactId, this.rx);

		return currentDoctor
			? { Id: currentDoctor.Id, Name: currentDoctor.Name, Licence: currentDoctor.Licence || this.license }
			: undefined;
	}
	get rxVersionFlow(): RxVersion {
		return this.getValue().rxVersionFlow;
	}
	get isProcedureFlow(): boolean {
		return !this.getValue().forceV0 && this.checkIsProcedureFlow(this.rxVersionFlow);
	}
	get isPatientAppDialogOpen(): boolean {
		return this.getValue().isPatientAppDialogOpen;
	}
	get isResendScan(): boolean {
		return this.getValue().isResendScan;
	}
	get userRole(): RoleTypeEnum {
		return this.getValue().userRole;
	}
	get isRxForLab(): boolean {
		return this.getValue().userRole === RoleTypeEnum.Lab;
	}
	get autoDisplayPrintViewInTheDOM(): boolean {
		return this.getValue().autoDisplayPrintViewInTheDOM ?? false;
	}
	get shouldValidateForSend(): boolean {
		return this.checkWhetherShouldValidateForSend(this.getValue());
	}
	get regulatoryDOBMask(): string | undefined {
		return this.getValue().rxConfiguration?.CompanyConfiguration?.RegulatorySettings?.DOBMask;
	}
	get companyCountryCode(): string | undefined {
		return this.getValue().rxConfiguration?.CompanyConfiguration?.CountryCode;
	}

	get printStyleName(): Maybe<string> {
		return this.getValue().printStyleName;
	}
	get isRxAttachmentsReady(): boolean {
		return this.getValue().isRxAttachmentsReady;
	}
	get apiEndpointFallback(): Maybe<string> {
		return this.getValue().rxAppApiEndpoint ?? this.getValue().apiEndpoint;
	}

	get patientGuidProvided() {
		return this.getValue().patientGuidProvided;
	}

	get viewMode() {
		return this.getValue().viewMode;
	}

	constructor(
		shellStore: ShellStore,
		private injector: Injector
	) {
		super(shellStore);
	}

	public getReadOnlyState(componentName: keyof RxComponents): Observable<boolean> {
		return combineLatest([this.readOnlyRules$, this.isReadOnly$]).pipe(
			map(([rules, isReadOnly]) => {
				return this.isSectionReadOnly(rules, componentName, isReadOnly);
			}),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}
	public getReadOnlyOrderState(componentName: keyof RxComponents): Observable<boolean> {
		return combineLatest([this.readOnlyRules$, this.isReadOnly$]).pipe(
			map(([rules, isReadOnly]) => {
				return this.isOrderSectionReadOnly(rules, componentName, isReadOnly);
			}),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}
	public getComponentReadOnlyState(componentName: keyof RxComponents): boolean {
		return this.isSectionReadOnly(this.readOnlyRules, componentName, this.isReadOnly);
	}

	public checkIsProcedureFlow(rxVersion: RxVersion): boolean {
		return rxVersion === RxVersion.ProcedureFlow;
	}

	private getCurrentDoctor(doctors: Maybe<DoctorsRxConfiguration[]>, contactId: Maybe<number>, rx: Maybe<RxModel>): Maybe<Doctor> {
		if (rx) {
			return rx.Doctor as Doctor;
		}

		return doctors?.find((doctor: DoctorsRxConfiguration) => doctor.Id === contactId);
	}

	private checkIsNewAndNotClonedRx(state: ShellState): boolean {
		return !state.orderId && !state.rxId && !state.clonedFromRxId;
	}

	private checkWhetherShouldValidateForSend(state: ShellState): boolean {
		return !state.isReadOnly && state.validationMode === 'ReadyForSend';
	}

	private isSectionReadOnly(rules: Maybe<RxComponents>, componentName: keyof RxComponents, isReadOnly: boolean): boolean {
		return rules?.hasOwnProperty(componentName) ? (rules[componentName] as boolean) : isReadOnly;
	}

	private isOrderSectionReadOnly(
		rules: RxComponents | null | undefined,
		componentName: keyof RxComponents,
		isReadOnly: boolean
	): boolean {
		return rules?.hasOwnProperty(componentName)
			? (rules[componentName] as boolean)
			: this.isSectionReadOnly(rules, 'order', isReadOnly);
	}
}
